import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getAccountVendors,
  getAccountRecurringBillById,
  createAccountRecurringBill,
  updateAccountRecurringBill,
} from '../core/_requests';
import SearchableSelect from '../../../shared/components/SearchableSelect'

export default function AccountsNewRecurringBill() {
  const navigate = useNavigate();
  const { id } = useParams();
  const profileId = id ? Number(id) : null;
  const isEditing = profileId !== null && !Number.isNaN(profileId);

  const [saving, setSaving] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(false);

  // Vendor dropdown data
  const [vendors, setVendors] = useState<any[]>([]);

  // Form state
  const [vendorId, setVendorId] = useState('');
  const [profileName, setProfileName] = useState('');
  const [frequency, setFrequency] = useState('Monthly');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState('');
  const [nextBillDate, setNextBillDate] = useState('');
  const [subTotal, setSubTotal] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [taxName, setTaxName] = useState('');
  const [taxAmount, setTaxAmount] = useState('');
  const [adjustment, setAdjustment] = useState('');
  const [notes, setNotes] = useState('');
  const [termsConditions, setTermsConditions] = useState('');
  const [status, setStatus] = useState('Active');

  // Computed total
  const computedSubTotal = Number(subTotal) || 0;
  const computedDiscount = Number(discountAmount) || 0;
  const computedTax = Number(taxAmount) || 0;
  const computedAdjustment = Number(adjustment) || 0;
  const computedTotal = computedSubTotal - computedDiscount + computedTax + computedAdjustment;

  useEffect(() => {
    getAccountVendors()
      .then((data) => setVendors(data || []))
      .catch((err) => console.error('Failed to load vendors:', err));
  }, []);

  useEffect(() => {
    if (!isEditing || profileId == null) return;
    setLoadingInitial(true);
    getAccountRecurringBillById(profileId)
      .then((data: any) => {
        if (data) {
          setVendorId(data.vendorId ? String(data.vendorId) : '');
          setProfileName(data.profileName || '');
          setFrequency(data.frequency || 'Monthly');
          setStartDate(data.startDate || new Date().toISOString().split('T')[0]);
          setEndDate(data.endDate || '');
          setNextBillDate(data.nextBillDate || '');
          setSubTotal(data.subTotal !== undefined ? String(data.subTotal) : '');
          setDiscountAmount(data.discountAmount !== undefined ? String(data.discountAmount) : '');
          setTaxName(data.taxName || '');
          setTaxAmount(data.taxAmount !== undefined ? String(data.taxAmount) : '');
          setAdjustment(data.adjustment !== undefined ? String(data.adjustment) : '');
          setNotes(data.notes || '');
          setTermsConditions(data.termsConditions || '');
          setStatus(data.status || 'Active');
          
          // Using termsConditions field for paymentTerms since model doesn't have it explicitly
          // Or we can just drop it, let's keep it simple.
        }
      })
      .catch((err) => console.error('Failed to load recurring bill:', err))
      .finally(() => setLoadingInitial(false));
  }, [isEditing, profileId]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vendorId || !profileName || !startDate) {
      alert('Please fill in Vendor, Profile Name, and Start Date.');
      return;
    }

    setSaving(true);
    const selectedVendor = vendors.find((v) => v.id === Number(vendorId));

    const payload = {
      vendorId: Number(vendorId),
      vendorName: selectedVendor ? selectedVendor.displayName : null,
      profileName,
      frequency,
      startDate,
      endDate: endDate || null,
      nextBillDate: nextBillDate || startDate,
      total: computedTotal,
      status,
      // Pass these down to the mock/real backend if it accepts them in JSON or future expansion
      subTotal: computedSubTotal,
      discountAmount: computedDiscount,
      taxName: taxName || null,
      taxAmount: computedTax,
      adjustment: computedAdjustment,
    };

    try {
      if (isEditing && profileId) {
        await updateAccountRecurringBill(profileId, payload);
      } else {
        await createAccountRecurringBill(payload);
      }
      navigate('/accounts/purchase/recurring-bills');
    } catch (err: any) {
      console.error('Save failed:', err);
      alert('Failed to save recurring bill');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all';

  if (loadingInitial) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-50">
        <p className="text-slate-500 font-medium">Loading Recurring Bill Profile...</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">
        
        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">
          
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6" id="main-scroll">
            <div className="max-w-5xl mx-auto pb-20">
              <div className="mb-6">
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                  Accounts / Purchase / Recurring Bills
                </div>
                <h1 className="text-2xl font-bold text-slate-900 leading-tight">
                  {isEditing ? 'Edit Recurring Bill' : 'New Recurring Bill'}
                </h1>
                <p className="text-sm text-slate-500">
                  {isEditing ? 'Update recurring bill profile' : 'Create a recurring bill profile'}
                </p>
              </div>

              <form onSubmit={handleSave} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
                <div className="mb-5">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                    Vendor Name <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect value={vendorId} onChange={(e) => setVendorId(e.target.value)} required className={inputClass}>
                    <option value="">Select a vendor...</option>
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.displayName}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>

                <div className="mb-5">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                    Profile Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    required
                    placeholder="e.g., Monthly Software Subscription"
                    className={inputClass}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Repeat Every <span className="text-red-500">*</span>
                    </label>
                    <SearchableSelect value={frequency} onChange={(e) => setFrequency(e.target.value)} required className={inputClass}>
                      <option value="Weekly">Weekly</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="Yearly">Yearly</option>
                    </SearchableSelect>
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Payment Terms
                    </label>
                    <SearchableSelect value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} className={inputClass}>
                      <option value="">Select...</option>
                      <option value="Due on Receipt">Due on Receipt</option>
                      <option value="Net 15">Net 15</option>
                      <option value="Net 30">Net 30</option>
                      <option value="Net 45">Net 45</option>
                    </SearchableSelect>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                      className={inputClass}
                    />
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>

                {isEditing && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="mb-5">
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                        Status
                      </label>
                      <SearchableSelect value={status} onChange={(e) => setStatus(e.target.value)} className={inputClass}>
                        <option value="Active">Active</option>
                        <option value="Stopped">Stopped</option>
                        <option value="Expired">Expired</option>
                      </SearchableSelect>
                    </div>
                    <div className="mb-5">
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                        Next Bill Date
                      </label>
                      <input
                        type="date"
                        value={nextBillDate}
                        onChange={(e) => setNextBillDate(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  </div>
                )}

                <hr className="my-6 border-slate-100" />

                {/* Totals Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Sub Total
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={subTotal}
                      onChange={(e) => setSubTotal(e.target.value)}
                      placeholder="0.00"
                      className={inputClass}
                    />
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Discount Amount
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={discountAmount}
                      onChange={(e) => setDiscountAmount(e.target.value)}
                      placeholder="0.00"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Tax Name
                    </label>
                    <input
                      type="text"
                      value={taxName}
                      onChange={(e) => setTaxName(e.target.value)}
                      placeholder="e.g. VAT"
                      className={inputClass}
                    />
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Tax Amount
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={taxAmount}
                      onChange={(e) => setTaxAmount(e.target.value)}
                      placeholder="0.00"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="mb-5 max-w-xs">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                    Adjustment
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={adjustment}
                    onChange={(e) => setAdjustment(e.target.value)}
                    placeholder="0.00"
                    className={inputClass}
                  />
                </div>

                <div className="flex justify-end mb-6">
                  <div className="w-72 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Sub Total</span>
                      <span className="font-semibold">{computedSubTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Discount</span>
                      <span>-{computedDiscount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Tax</span>
                      <span>{computedTax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Adjustment</span>
                      <span>{computedAdjustment.toFixed(2)}</span>
                    </div>
                    <hr className="border-slate-200" />
                    <div className="flex justify-between text-base font-bold text-blue-600">
                      <span>Total ($) / Cycle</span>
                      <span>{computedTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <hr className="my-6 border-slate-100" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Notes
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      placeholder="Internal notes"
                      className={`${inputClass} resize-none`}
                    ></textarea>
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Terms & Conditions
                    </label>
                    <textarea
                      value={termsConditions}
                      onChange={(e) => setTermsConditions(e.target.value)}
                      rows={3}
                      placeholder="Terms & conditions"
                      className={`${inputClass} resize-none`}
                    ></textarea>
                  </div>
                </div>

                <div className="pt-6 mt-6 border-t border-slate-100 flex items-center gap-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow transition-all disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : isEditing ? 'Update Profile' : 'Save Profile'}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="px-6 py-2.5 text-slate-500 hover:text-slate-700 text-sm font-bold transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
            
          </div>
        </main>
      </div>
      
    </>
  );
}

